import { CopyOutlined } from '@ant-design/icons';
import { Flex, Input, message, Modal, Spin } from 'antd';
import { useContext, useEffect, useState } from 'react';

import { useGetOrCreateYookassaPaymentMutation } from '@app/api/ClientPayments';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers/NotificationProvider';
import { handleCatchError } from '@app/services';

import type { YookassaPaymentWithEmailModalProps } from '../types';

export const YookassaPaymentWithEmailModal: React.FC<
  YookassaPaymentWithEmailModalProps
> = ({
  isYookassaPaymentWithEmailModalOpen,
  setIsYookassaPaymentWithEmailModalOpen,
  yookassaPaymentLinkParams,
  setYookassaPaymentLinkParams,
  fetchPayments,
  setIsNeedToCreateYooKassaPayment,
}) => {
  const { showNotification } = useContext(NotificationContext);
  const { activeBranchId: ActiveBranchId } = useUserPrint();

  const [paymentLink, setPaymentLink] = useState<string>('');

  const [getOrCreateYookassaPayment, { isLoading }] =
    useGetOrCreateYookassaPaymentMutation();

  useEffect(() => {
    // Ссылку запрашиваем в обоих случаях: для нового платежа бэк создаёт его и
    // выдаёт токен, для платежа в статусе awaiting_email — переиздаёт токен по
    // clientPaymentsId. Собственную ссылку фронт больше не строит (us/10744).
    const fetchYooKassaPaymentLink = async () => {
      if (!isYookassaPaymentWithEmailModalOpen || !yookassaPaymentLinkParams) {
        return;
      }

      if (!ActiveBranchId) {
        showNotification('Филиал не был распознан');

        return;
      }

      try {
        const { ConfirmationUrl } = await getOrCreateYookassaPayment({
          ...yookassaPaymentLinkParams,
          ActiveBranchId,
        }).unwrap();

        setPaymentLink(ConfirmationUrl ?? '');
        setIsNeedToCreateYooKassaPayment(false);
        await fetchPayments();
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    };

    fetchYooKassaPaymentLink();
  }, [
    ActiveBranchId,
    fetchPayments,
    getOrCreateYookassaPayment,
    isYookassaPaymentWithEmailModalOpen,
    setIsNeedToCreateYooKassaPayment,
    showNotification,
    yookassaPaymentLinkParams,
  ]);

  const handleCancel = () => {
    setIsYookassaPaymentWithEmailModalOpen(false);
    setYookassaPaymentLinkParams(null);
    setPaymentLink('');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(paymentLink);
      message.success('Скопировано!');
    } catch (err) {
      message.error('Не удалось скопировать');
    }
  };

  return (
    <Modal
      open={isYookassaPaymentWithEmailModalOpen}
      onCancel={handleCancel}
      title="Оплата через ЮKassa"
      footer={null}
    >
      <Flex vertical gap={16}>
        <h4>Ссылка на оплату</h4>
        {isLoading ? (
          <Spin />
        ) : (
          <Input
            value={paymentLink}
            readOnly
            onClick={(e) => e.currentTarget.select()}
            suffix={
              <CopyOutlined
                onClick={handleCopy}
                style={{ cursor: 'pointer' }}
              />
            }
          />
        )}
      </Flex>
    </Modal>
  );
};
